import router from '@/router';
import { nextTick } from 'vue';
import { defineStore } from 'pinia';
import type { AxiosError } from 'axios';
import { cookies } from '@/plugins/cookies';
import {
  ForgotPasswordBody,
  InviteAccept,
  InviteOrgUserBody,
  LoginBody,
  RegisterBody,
  ResendInviteData,
  ResendVerificationOutcome,
  ResetPasswordBody,
  User,
  UserOrganisation,
  UserPermissions,
} from '@/types/api/auth.types';
import {
  emailConfirm,
  forgotPassword,
  getCurrentUserPermissions,
  invitationConfirm,
  invitationValid,
  inviteUserToOrg,
  login,
  register,
  resendConfirmation,
  resendInvitation,
  resetPassword,
  resetTokenValid,
} from '@/api/auth.service';
import i18n from '@/plugins/vue-i18n';
import { showResendInviteToast } from '@/common/utils/user-role-toast.utils';
import { useOrgStore } from '@/stores/organisations.store';
import { useUserStore } from '@/stores/users.store';
import { OrgRoles } from '@/types/api/roles.types';
import { clearAllPiniaStores } from '@/common/utils/stateManagement';
import {
  DEFAULT_RETRY_AFTER_SECONDS,
  isSuccess,
  retryAfterSeconds,
} from '@/common/utils/http.utils';
import { selectedOrgId, selectedOrgRoles } from '@/common/utils/role.utils';
import { ownsPendingOnboarding, PENDING_ONBOARDING_KEY } from '@/common/utils/onboarding.utils';
// Wired into every code path that writes `loggedInUser` (state init from
// localStorage, setUser, setSelectedOrg, setUserRolePermissions) so the rest of
// the app never sees the backend's split-membership shape — see the helper's
// JSDoc for the full rationale.
import { normalizeUserRoles } from '@/common/utils/user-roles.utils';
import { analyticsService, hasAcceptedCurrentConsent } from '@/plugins/post-hog';
import { isPostHogEnabled } from '@/utils/flags';
import { usePermissionStore } from '@/stores/permission.store';
import { usePusherStore } from '@/stores/pusher.store';
import { useUserbackStore } from '@/stores/userback.store';
import { landingRouteFor, landingRouteName } from '@/common/utils/landing.utils';

const { t } = i18n.global;

/**
 * Org-level roles that give a user something to do in an organisation on arrival. An
 * allowlist, not "anything but ORG_MEMB": VendorControl roles share this array and must not
 * qualify a BudgetControl landing org.
 */
const ORG_ROLES_WITH_STANDALONE_ACCESS = new Set<string>([
  OrgRoles.ORG_ADMIN,
  OrgRoles.ORG_OWN,
  OrgRoles.ORG_FO,
  OrgRoles.ORG_FA,
]);

/**
 * Whether the user holds one of {@link ORG_ROLES_WITH_STANDALONE_ACCESS} in this org. Reads
 * every `roles.organisations` entry for the id: the API returns one per org-level role, so a
 * first-match lookup misses any role listed after the first.
 */
const hasStandaloneOrgAccess = (user: User, organisationId: string): boolean =>
  (user.roles?.organisations ?? [])
    .filter((org) => org.id === organisationId)
    .some((org) => org.roles.some((role) => ORG_ROLES_WITH_STANDALONE_ACCESS.has(role)));

/**
 * The organisation a session should be active in: the one already open, else the pinned one,
 * else the first the user can act in, else the first they belong to. `undefined` only when the
 * user holds no memberships at all.
 */
const resolveSelectedOrg = (user: User, activeOrgId?: string): UserOrganisation | undefined => {
  const organisations = user.organisations ?? [];

  return (
    (activeOrgId ? organisations.find((org) => org.id === activeOrgId) : undefined) ||
    organisations.find((org) => org.primary) ||
    organisations.find((org) => hasStandaloneOrgAccess(user, org.id)) ||
    organisations[0]
  );
};

export const useAuthStore = defineStore('auth', {
  state: () => ({
    accessToken: null as string | null,
    accessExpiry: null as string | null,
    refreshToken: null as string | null,
    refreshExpiry: null as string | null,
    loggedInUser: normalizeUserRoles((JSON.parse(localStorage.getItem('user')!) as User) || null),
    resetPasswordEmail: localStorage.getItem('resetEmail') ?? '',
    resendEmailEmail: localStorage.getItem('resendEmailEmail') ?? '',
    inviteInvalid: false as boolean,
    analyticsSyncCheckedOnLogin: false as boolean,
    unifiedAuthEnabled: false as boolean,
  }),

  getters: {
    isLoggedIn(): boolean {
      return !!this.accessToken;
    },
  },

  actions: {
    async validateInvite(code: string) {
      const response = await invitationValid(code);
      return response.data.data;
    },

    async confirmInvite(userDetails: InviteAccept) {
      const response = await invitationConfirm(userDetails);
      // if: new user, confirmation logs them in
      if (userDetails.password) {
        this.setAllTokens(response.data.data.token);
        this.setUser(response.data.data.user);
        usePusherStore().connect();

        if (isPostHogEnabled) {
          // New user consents by accepting T's & C's on registration
          analyticsService.setConsent('true');

          analyticsService.capture('user_registered', {
            user_role: this.loggedInUser?.selectedOrg?.userRole,
            user_roles: selectedOrgRoles.value.join(','),
            org_id: this.loggedInUser?.selectedOrg?.id,
            org_name: this.loggedInUser?.selectedOrg?.name,
          });
        }

        await this.getUserPermissions();

        await router.push(landingRouteFor(landingRouteName()));

        await this.getUserPermissions();
      }
    },

    async register(userDetails: RegisterBody) {
      // Context: Registration no longer issues a session (TCH-469). The account is created
      // inactive until the email is confirmed, so there are no tokens to store; send the user
      // to the verification step, where following the emailed link activates the account. The
      // org-setup onboarding it used to run inline now happens on the first sign-in, gated by
      // this marker (see the router's pending-onboarding guard). The marker holds the address
      // that registered, so it only ever routes that user.
      await register(userDetails);
      this.setResendEmailEmail(userDetails.email);
      localStorage.setItem(PENDING_ONBOARDING_KEY, userDetails.email);

      await router.push({ name: 'account-setup' });
    },

    // Context: The marker survives until onboarding is actually finished, not merely entered
    // (TCH-469), so a user who abandons the workspace-type choice is routed back into it on
    // their next sign-in rather than being left with no organisation. Every exit from
    // /account-setup into the app must call this, or the router guard will redirect back.
    // It clears only the signed-in user's own marker, leaving another user's registration
    // still owed its onboarding.
    completeOnboarding() {
      if (!ownsPendingOnboarding(this.loggedInUser?.email)) {
        return;
      }

      localStorage.removeItem(PENDING_ONBOARDING_KEY);
      cookies.remove(PENDING_ONBOARDING_KEY);
    },

    async confirmEmail(code: string) {
      const response = await emailConfirm(code);
      if (isSuccess(response?.status ?? 0)) {
        // Context: A self-registration confirms while signed out (TCH-469), so only refresh
        // the cached user when there is an active session; otherwise there is nothing to update.
        if (this.loggedInUser) {
          this.setUser({ ...this.loggedInUser, emailConfirmed: true });
        }
        return true;
      }
      return false;
    },

    // Context: the resend cooldown is enforced by the API, not this client (TCH-469), so a
    // refusal inside the window is an expected outcome rather than an error, and the wait it
    // reports is what the resend control counts down against.
    async resendConfirm(email: string): Promise<ResendVerificationOutcome> {
      try {
        const response = await resendConfirmation(email);
        return {
          sent: isSuccess(response?.status ?? 0),
          cooldownSeconds: DEFAULT_RETRY_AFTER_SECONDS,
        };
      } catch (error) {
        if ((error as AxiosError)?.response?.status === 429) {
          return { sent: false, cooldownSeconds: retryAfterSeconds(error) };
        }
        throw error;
      }
    },

    async resendInvitation(resendInviteData: ResendInviteData, email: string) {
      const response = await resendInvitation(resendInviteData);
      const succeeded = isSuccess(response?.status ?? 0);

      if (succeeded) {
        showResendInviteToast(email);
      }

      return succeeded;
    },

    async login(loginDetails: LoginBody) {
      const response = await login(loginDetails);
      const redirectPath = localStorage.getItem('redirectPath');

      this.setAllTokens(response.data.data.token);
      this.setUser(response.data.data.user);
      // A logout in this tab disconnected the last client, and a sign-in that stays in the tab
      // (unified auth off, or no auth app to hand off to) never reloads to start one at bootstrap.
      usePusherStore().connect();

      if (isPostHogEnabled) {
        setTimeout(async () => {
          const userId = this.loggedInUser?.id;

          if (!userId) {
            // If no user id is available, fallback to syncing consent without user-scoped storage.
            await analyticsService.syncConsentFromRemote(true);
            return;
          }

          // Version-aware: matches a consent record at the CURRENT version (stored
          // as the version string, e.g. '2'), not just the legacy 'true'.
          if (hasAcceptedCurrentConsent(userId)) {
            analyticsService.identify(true);
          } else {
            // Attempt to sync from PostHog before deciding to show the banner
            await analyticsService.syncConsentFromRemote(true);
          }
        }, 300);
      }

      await this.getUserPermissions();

      if (redirectPath) {
        await router.push(redirectPath);
        localStorage.removeItem('redirectPath');
      } else {
        await router.push(landingRouteFor(landingRouteName()));
      }
    },

    async forgotPassword(email: ForgotPasswordBody) {
      const response = await forgotPassword(email);
      this.setResetEmail(email.email);

      return response.status;
    },

    async validateResetToken(token: string) {
      const response = await resetTokenValid(token);
      return response.status;
    },

    async resetPassword(data: ResetPasswordBody) {
      const response = await resetPassword(data);
      return response.status;
    },

    async getUserPermissions() {
      const userStore = useUserStore();

      const response = await getCurrentUserPermissions();
      await userStore.getCurrentUser();

      this.setUserPermissions(response.data.data);
      this.setUserRolePermissions();

      // Nothing else fetches the organisation list on a clean boot, so a login payload that
      // arrived without it leaves the session with no active org at all — and every org-scoped
      // permission check resolves through `selectedOrg.id`.
      //
      // Best-effort: this recovers an already-degraded session, and `login()` awaits this
      // method before it routes, so letting the fetch reject would strand a user who has just
      // authenticated on the login screen instead of merely limiting what they can edit.
      if (!this.loggedInUser?.selectedOrg?.id) {
        try {
          await useOrgStore().getCurrentUserOrgs();
        } catch (error) {
          console.error('Failed to resolve the active organisation:', error);
        }
      }

      // Context: the roles and the active org are current from here, so a screen holding off on
      // the stored ones may decide (PE-1081). An org-scoped check reads no rights without an org,
      // so this waits for the recovery above, then a tick for `casl.utils.ts` to rebuild the ability.
      await nextTick();
      usePermissionStore().setRolesRefreshed();
    },

    async inviteUsersToOrg(data: InviteOrgUserBody) {
      await inviteUserToOrg(data);

      analyticsService.capture('org_user_invited', {
        org_id: this.loggedInUser?.selectedOrg?.id,
        org_name: this.loggedInUser?.selectedOrg?.name,
        user_role: data.users[0]?.role,
      });
    },

    async logout() {
      const permissionStore = usePermissionStore();
      const shouldRedirectToAuthApp = this.unifiedAuthEnabled;

      this.accessToken = null;
      this.refreshToken = null;

      this.setAccessToken('');

      cookies.remove('accessToken');
      cookies.remove('refreshToken');
      cookies.remove('user');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('projectRoles');
      localStorage.removeItem('siteRoles');
      localStorage.removeItem('dontShowConfirmDialogAgain');

      analyticsService.reset();
      this.removeUser();

      permissionStore.resetContext();
      permissionStore.setUser(null);
      // Closed rather than left to the reset, which would forget the socket while it stays open
      // and keeps delivering the signed-out session's channels.
      usePusherStore().disconnectPusher();
      clearAllPiniaStores();

      if (shouldRedirectToAuthApp) {
        const authBaseUri = import.meta.env.VITE_APP_AUTH_BASE_URI as string;
        if (authBaseUri) {
          globalThis.location.href = authBaseUri;
          return;
        }
      }

      await router.push({ name: 'login' });
    },

    setAccessToken(accessToken: string) {
      this.accessToken = accessToken;
      localStorage.setItem('accessToken', accessToken);
    },

    setRefreshToken(refreshToken: string) {
      this.refreshToken = refreshToken;
      localStorage.setItem('refreshToken', refreshToken);
    },

    setAllTokens(token: {
      accessToken: string;
      accessExpiry: string;
      refreshToken: string;
      refreshExpiry: string;
    }) {
      this.accessToken = token.accessToken;
      this.accessExpiry = token.accessExpiry;

      this.refreshToken = token.refreshToken;
      this.refreshExpiry = token.refreshExpiry;

      cookies.set('accessToken', token.accessToken, {
        expires: new Date(this.accessExpiry),
      });
      cookies.set('refreshToken', token.refreshToken, {
        expires: new Date(this.refreshExpiry),
      });
      localStorage.setItem('accessToken', token.accessToken);
      localStorage.setItem('refreshToken', token.refreshToken);
    },

    setUser(user: User) {
      const orgStore = useOrgStore();
      this.loggedInUser = user;

      if (this.loggedInUser.organisations?.length > 0) {
        // A user has no primary org until they pin one, so without the access-aware step an
        // arbitrary, API-ordered first entry decides the landing org — which can be an org the
        // user holds no authority in.
        this.loggedInUser.selectedOrg = resolveSelectedOrg(
          this.loggedInUser,
          orgStore.currentOrgDetails?.id,
        );
      }

      // Normalize after selectedOrg is set so userRole promotion can see the
      // active org. Running this earlier would skip the promotion branch and
      // persist a stale `selectedOrg.userRole` (e.g. 'VC_SUPER') to localStorage.
      this.loggedInUser = normalizeUserRoles(this.loggedInUser);

      localStorage.setItem('user', JSON.stringify(this.loggedInUser));

      if (this.loggedInUser.id) {
        useUserbackStore().identify(this.loggedInUser.id, {
          name: this.loggedInUser.firstName,
          email: this.loggedInUser.email,
        });
      }
    },

    async setSelectedOrg(organisation: UserOrganisation) {
      const permissionStore = usePermissionStore();

      this.loggedInUser.selectedOrg = this.loggedInUser.organisations.find(
        (org: UserOrganisation) => org.id === organisation.id,
      );
      this.loggedInUser = normalizeUserRoles(this.loggedInUser);
      localStorage.setItem('user', JSON.stringify(this.loggedInUser));

      permissionStore.resetContext();
      permissionStore.setUser(
        this.loggedInUser?.id && this.loggedInUser.roles?.organisations
          ? (this.loggedInUser as unknown as import('@boqnet/permissions').User)
          : null,
      );
    },

    setUserOrgs(currentUserOrgs: Partial<UserOrganisation[]>) {
      this.loggedInUser.organisations = currentUserOrgs as UserOrganisation[];

      // The first point the org list is known when the login payload arrived without one, so
      // adopt an active org here rather than waiting for a manual org switch. Passing the
      // current id keeps a still-held org active — which is what stops pinning from switching
      // orgs under the user — while swapping in that org's refreshed membership object, so
      // `selectedOrg` can't serve a name, primary flag or userRole the list has since changed.
      // An org missing from the refreshed list (membership revoked since login) re-resolves.
      this.loggedInUser.selectedOrg = resolveSelectedOrg(
        this.loggedInUser,
        this.loggedInUser.selectedOrg?.id,
      );
      // A `selectedOrg` write site owes the same normalisation as the others — pass 2 is what
      // keeps the `userRole` scalar behind analytics and display aligned with the strongest
      // role held in the active org. Idempotent, so it is safe on the unchanged path too.
      this.loggedInUser = normalizeUserRoles(this.loggedInUser);

      localStorage.setItem('user', JSON.stringify(this.loggedInUser));
    },

    /**
     * Optimistically grant roles on an org in the array role model
     * (`roles.organisations[].roles`) — mirrors the scalar `userRole` seed used
     * on org creation so array-based checks (`hasOrgPermissions`) see the grant
     * immediately, without waiting for the backend permissions payload. The next
     * `getUserPermissions()` reconciles with the backend. Idempotent — unions
     * with any existing roles for the org.
     */
    // Write the logged-in user's role set for an org and re-normalise/persist.
    // `merge: true` unions with existing roles (additive seed); the default
    // replaces them (authoritative set). Keeps loggedInUser — which drives
    // hasOrgPermissions/CASL and is persisted — in sync without a fresh login.
    setOrgRoles(
      organisationId: string,
      roles: OrgRoles[],
      { merge = false }: { merge?: boolean } = {},
    ) {
      if (!this.loggedInUser) return;

      const existingOrgs = this.loggedInUser.roles?.organisations ?? [];
      const hasOrg = existingOrgs.some((org) => org.id === organisationId);

      const organisations = hasOrg
        ? existingOrgs.map((org) =>
            org.id === organisationId
              ? { ...org, roles: merge ? [...new Set([...org.roles, ...roles])] : [...roles] }
              : org,
          )
        : [...existingOrgs, { id: organisationId, roles: [...roles], sites: [], projects: [] }];

      this.loggedInUser = normalizeUserRoles({
        ...this.loggedInUser,
        roles: { boq: this.loggedInUser.roles?.boq ?? [], organisations },
      });

      localStorage.setItem('user', JSON.stringify(this.loggedInUser));
    },

    // Additive: seed roles onto an org without dropping existing ones (e.g.
    // seeding ORG_ADMIN on a freshly created org).
    seedOrgRoles(organisationId: string, roles: OrgRoles[]) {
      this.setOrgRoles(organisationId, roles, { merge: true });
    },

    setUserPermissions(permissions: UserPermissions[]) {
      this.loggedInUser = { ...this.loggedInUser, permissions };

      localStorage.setItem('user', JSON.stringify(this.loggedInUser));
    },

    setUserRolePermissions() {
      const userStore = useUserStore();

      if (!userStore.currentUser || !this.loggedInUser) return;

      const updatedRoles = userStore.currentUser.roles;

      this.loggedInUser = normalizeUserRoles({ ...this.loggedInUser, roles: updatedRoles });

      if (this.loggedInUser.selectedOrg) {
        const orgIdToUpdate = this.loggedInUser.selectedOrg.id;

        // Read from the normalized store state, not the raw `updatedRoles`
        // param — `updatedRoles` may carry duplicate org entries that would
        // cause .find() to pick the wrong (e.g. VC-only) membership and
        // overwrite the userRole promotion done in normalizeUserRoles().
        const updatedOrgPermissions = this.loggedInUser.roles?.organisations.find(
          (org) => org.id === orgIdToUpdate,
        );

        if (updatedOrgPermissions && updatedOrgPermissions.roles.length > 0) {
          let newPrimaryRole;

          // Prioritize ORG_ADMIN if it exists in the array
          if (updatedOrgPermissions.roles.includes(OrgRoles.ORG_ADMIN)) {
            newPrimaryRole = OrgRoles.ORG_ADMIN;
          } else {
            // Otherwise, fall back to the first role in the array
            newPrimaryRole = updatedOrgPermissions.roles[0];
          }

          if (newPrimaryRole) this.updateLoggedInUserOrgRole(orgIdToUpdate, newPrimaryRole);
        }
      }

      localStorage.setItem('user', JSON.stringify(this.loggedInUser));
    },

    /**
     * @param {string} organisationId - The ID of the organisation to update.
     * @param {string} newRole - The new role to set.
     */
    updateLoggedInUserOrgRole(organisationId: string, newRole: string) {
      if (!this.loggedInUser) return;

      let wasUpdated = false;

      const orgInArray = this.loggedInUser.organisations?.find(
        (org: UserOrganisation) => org.id === organisationId,
      );
      if (orgInArray && orgInArray.userRole !== newRole) {
        orgInArray.userRole = newRole;
        wasUpdated = true;
      }

      if (
        this.loggedInUser.selectedOrg?.id === organisationId &&
        this.loggedInUser.selectedOrg.userRole !== newRole
      ) {
        this.loggedInUser.selectedOrg.userRole = newRole;
        wasUpdated = true;
      }

      if (wasUpdated) {
        localStorage.setItem('user', JSON.stringify(this.loggedInUser));
      }
    },

    removeUser() {
      localStorage.removeItem('user');
    },

    setResetEmail(email: string) {
      this.resetPasswordEmail = email;
      localStorage.setItem('resetEmail', this.resetPasswordEmail);
    },

    removeResetEmail() {
      localStorage.removeItem('resetEmail');
    },

    setResendEmailEmail(email: string) {
      this.resendEmailEmail = email;
      localStorage.setItem('resendEmailEmail', this.resendEmailEmail);
    },

    removeResendEmailEmail() {
      localStorage.removeItem('resendEmailEmail');
    },

    setUnifiedAuth(enabled: boolean) {
      this.unifiedAuthEnabled = enabled;
    },

    clearStore() {
      this.accessToken = null;
      this.refreshToken = null;
    },
  },
});
